import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DataTable from '../components/DataTable';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PasswordModal from '../components/PasswordModal';
import { verifyAdminPassword } from '../utils/passwordUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CatalogPage = () => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [brandSelection, setBrandSelection] = useState('');
  const [customBrand, setCustomBrand] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    machine_type: 'MFP A4',
    machine_color: 'BLACK',
    consumables: ''
  });

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalog(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης καταλόγου');
    } finally {
      setLoading(false);
    }
  };

  // Get unique brands from existing catalog + OTHER option
  const getAvailableBrands = () => {
    const uniqueBrands = [...new Set(catalog.map(item => item.brand))].sort();
    return [...uniqueBrands, 'OTHER'];
  };

  const handleBrandChange = (value) => {
    setBrandSelection(value);
    if (value !== 'OTHER') {
      setFormData({ ...formData, brand: value });
      setCustomBrand('');
    } else {
      setCustomBrand('');
      setFormData({ ...formData, brand: '' });
    }
  };

  const handleCustomBrandChange = (value) => {
    setCustomBrand(value);
    setFormData({ ...formData, brand: value.toUpperCase() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model.trim()) {
      toast.error('⚠️ ΜΑΡΚΑ ΚΑΙ ΜΟΝΤΕΛΟ ΕΙΝΑΙ ΥΠΟΧΡΕΩΤΙΚΑ!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const saveData = {
        ...formData,
        brand: formData.brand.toUpperCase(),
        model: formData.model.trim().toUpperCase(),
        consumables: formData.consumables.trim().toUpperCase()
      };
      
      if (editingId) {
        await axios.put(`${API_URL}/catalog/${editingId}`, saveData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Το μοντέλο ενημερώθηκε επιτυχώς!');
      } else {
        await axios.post(`${API_URL}/catalog`, saveData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Το μοντέλο προστέθηκε στον κατάλογο!');
      }
      
      resetForm();
      fetchCatalog();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('UNIQUE')) {
        toast.error('❌ ΑΥΤΟΣ Ο ΣΥΝΔΥΑΣΜΟΣ ΜΑΡΚΑ + ΜΟΝΤΕΛΟ ΥΠΑΡΧΕΙ ΗΔΗ!');
      } else {
        toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
      }
    }
  };

  const handleEdit = (item) => {
    setPendingAction({ type: 'edit', item });
    setShowPasswordModal(true);
  };

  const executeEdit = (item) => {
    setFormData({
      brand: item.brand,
      model: item.model,
      machine_type: item.machine_type,
      machine_color: item.machine_color,
      consumables: item.consumables || ''
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setPendingAction({ type: 'delete', id });
    setShowPasswordModal(true);
  };

  const executeDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/catalog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Το μοντέλο διαγράφηκε!');
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα διαγραφής');
    }
  };

  const handlePasswordConfirm = async (password) => {
    const isValid = await verifyAdminPassword(password);
    if (isValid) {
      setShowPasswordModal(false);
      if (pendingAction?.type === 'edit') {
        executeEdit(pendingAction.item);
      } else if (pendingAction?.type === 'delete') {
        await executeDelete(pendingAction.id);
      }
      setPendingAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      machine_type: 'MFP A4',
      machine_color: 'BLACK',
      consumables: ''
    });
    setBrandSelection('');
    setCustomBrand('');
    setEditingId(null);
    setShowForm(false);
  };

  const columns = [
    { key: 'brand', label: 'ΜΑΡΚΑ' },
    { key: 'model', label: 'ΜΟΝΤΕΛΟ' },
    { key: 'machine_type', label: 'ΤΥΠΟΣ' },
    { key: 'machine_color', label: 'ΧΡΩΜΑ' },
    { 
      key: 'consumables', 
      label: 'ΣΧΟΛΙΟ',
      render: (item) => item.consumables ? (
        <span className="text-sm text-gray-600">{item.consumables.substring(0, 50)}...</span>
      ) : '-'
    }
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Κατάλογος Μηχανημάτων</h1>
          <p className="text-gray-600">Μοντέλα προς ενοικίαση - ΤΑ ΜΗΧΑΝΗΜΑΤΑ ΠΡΕΠΕΙ ΠΡΩΤΑ ΝΑ ΥΠΑΡΧΟΥΝ ΕΔΩ</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {showForm ? 'Ακύρωση' : 'Νέο Μοντέλο'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Επεξεργασία Μοντέλου' : 'Νέο Μοντέλο στον Κατάλογο'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>ΜΑΡΚΑ *</Label>
                  <select
                    value={brandSelection}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                    required
                  >
                    <option value="">-- ΕΠΙΛΟΓΗ --</option>
                    {getAvailableBrands().map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                  {brandSelection === 'OTHER' && (
                    <Input
                      value={customBrand}
                      onChange={(e) => handleCustomBrandChange(e.target.value)}
                      placeholder="ΝΕΑ ΜΑΡΚΑ (ΚΕΦΑΛΑΙΑ)"
                      className="mt-2"
                      required
                    />
                  )}
                </div>
                <div>
                  <Label>ΜΟΝΤΕΛΟ *</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Π.χ. ECOSYS M2040dn"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>ΤΥΠΟΣ *</Label>
                  <select
                    value={formData.machine_type}
                    onChange={(e) => setFormData({ ...formData, machine_type: e.target.value })}
                    className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="MFP A4">MFP A4</option>
                    <option value="MFP A3">MFP A3</option>
                    <option value="PRINTER A4">PRINTER A4</option>
                    <option value="PRINTER A3">PRINTER A3</option>
                  </select>
                </div>
                <div>
                  <Label>ΧΡΩΜΑ ΕΚΤΥΠΩΣΗΣ *</Label>
                  <select
                    value={formData.machine_color}
                    onChange={(e) => setFormData({ ...formData, machine_color: e.target.value })}
                    className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="BLACK">BLACK</option>
                    <option value="COLOR">COLOR</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>ΣΧΟΛΙΟ / ΑΝΑΛΩΣΙΜΑ</Label>
                <textarea
                  value={formData.consumables}
                  onChange={(e) => setFormData({ ...formData, consumables: e.target.value })}
                  className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                  rows="3"
                  placeholder="Π.χ. TONER TK-1150, DRUM DK-1150"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingId ? '💾 ΑΠΟΘΗΚΕΥΣΗ ΑΛΛΑΓΩΝ' : '✅ ΑΠΟΘΗΚΕΥΣΗ ΣΤΟΝ ΚΑΤΑΛΟΓΟ'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Ακύρωση
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Φόρτωση...</div>
          ) : catalog.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                Ο ΚΑΤΑΛΟΓΟΣ ΕΙΝΑΙ ΚΕΝΟΣ. ΠΡΟΣΘΕΣΤΕ ΜΟΝΤΕΛΑ ΕΔΩ ΓΙΑ ΝΑ ΤΑ ΧΡΗΣΙΜΟΠΟΙΗΣΕΤΕ ΣΤΑ ΜΗΧΑΝΗΜΑΤΑ.
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Προσθήκη Πρώτου Μοντέλου
              </Button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={catalog}
              actions={(row) => (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(row)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(row.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              )}
            />
          )}
        </CardContent>
      </Card>
      
      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Επιβεβαίωση Κωδικού Admin"
      />
    </div>
  );
};

export default CatalogPage;
